import React, { useContext, useState } from 'react';
import i18n from '@/utils/i18n';
import ClassicLoadingSpinner from '../../components/LoadingSpinner/ClassicLoadingSpinner';
import { useFindLocationQuery } from '../../graphql/queries/findItemLocation.generated';
import { useUpdateItemLocationMutation } from '../../graphql/mutations/updateItemLocation.generated';
import { useDeleteItemLocationMutation } from '../../graphql/mutations/deleteItemLocation.generated';
import { useHistory } from 'react-router';
import { FaLock, FaSave, FaTrash } from 'react-icons/fa';
import { LayoutStoreInstance } from '../../LayoutStore';
import { ItemLocationWithEventAccountFragment } from '../../graphql/fragments/itemLocationWithEventAccount.generated';
import { Link } from 'react-router-dom';
import {
  Button,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from 'reactstrap';
import OrderFulfillmentsTableWithFilter from './../Events/components/OrderFulfillmentsTableWithFilter';
import { useGetItemLocationStockQuery } from '../../graphql/queries/getItemLocationStock.generated';
import { ContentSection } from '../../components/Layout/ContentSection';
import { MainHeader } from '../../components/Layout/MainHeader';
import { ItemLocationStockView } from './components/ItemLocationStockView';
import { ItemLocationContainerStationView } from './components/ItemLocationContainerStationView';
import { useGetItemLocationTypesQuery } from '../../graphql/queries/getItemLocationTypes.generated';
import { OrderFulfillmentsTableHeaderSort } from '../Events/components/OrderFulfillmentsTable';
import { ViewerContext } from '../../providers/ViewerContextProvider';
import { Select } from '@mantine/core';
import { useAllEventsPaginatedQuery } from '../../graphql/queries/allEventsPaginated.generated';
import { useGetDeliveryAddressesForEventQuery } from '../../graphql/queries/getDeliveryAddressesForEvent.generated';
import { useLinkItemLocationToDeliveryAddressMutation } from '../../graphql/mutations/linkItemLocationToDeliveryAddress.generated';
import { useUnlinkItemLocationFromDeliveryAddressMutation } from '../../graphql/mutations/unlinkItemLocationFromDeliveryAddress.generated';
import { Routes } from '@/utils/routes';
import AssetsTable from './components/AssetsTable';
import { Role } from '../../contants';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';

const OfTableHeaderSort: OrderFulfillmentsTableHeaderSort[] = [
  'event',
  'eventAccount',
  'signer',
  'timestamp',
  'type',
  'status',
  'itemCount',
  'detail',
];

type ItemLocationDetailViewProps = {
  match: {
    params: { locationId: string | null };
  };
};

const ItemLocationDetailView = ({
  match: {
    params: { locationId },
  },
}: ItemLocationDetailViewProps) => {
  const context = useContext(ViewerContext);
  const readOnly = context.role === Role.eventCleaningEmployee;
  const history = useHistory();
  const [itemLocation, setItemLocation] =
    useState<ItemLocationWithEventAccountFragment>();

  useDocumentTitle('PageTitles:itemLocation', itemLocation?.name);
  const [itemLocationType, setItemLocationType] = useState('');
  const [selectedDeliveryAddressId, setSelectedDeliveryAddressId] =
    useState('');
  const [linkedDeliveryAddressId, setLinkedDeliveryAddressId] = useState<
    string | null
  >(null);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<string | null>(null);
  const [editEvent, setEditEvent] = useState(false);

  const { loading } = useFindLocationQuery({
    skip: locationId == null,
    variables: { id: locationId || '' },
    onCompleted: (d) => {
      if (d.itemLocation.id) {
        setItemLocation(d.itemLocation);
        setItemLocationType(d.itemLocation.type || 'Event');
        const initialDeliveryAddressId =
          d.itemLocation.delivery_address?.id || '';
        setSelectedDeliveryAddressId(initialDeliveryAddressId);
        setLinkedDeliveryAddressId(initialDeliveryAddressId || null);
      }
    },
  });

  const { data: itemLocationTypesResponse, loading: itemLocationTypesLoading } =
    useGetItemLocationTypesQuery();
  const eventId = itemLocation?.event?.id || '';
  const { data: deliveryAddressesResponse, loading: deliveryAddressesLoading } =
    useGetDeliveryAddressesForEventQuery({
      variables: { event_id: eventId },
      skip: !eventId,
    });

  const {
    data: stock,
    loading: loadingStock,
    error: stockError,
  } = useGetItemLocationStockQuery({
    skip: locationId == null,
    variables: { item_location_id: locationId || '' },
  });

  const { loading: loadingEvents, data: eventsData } =
    useAllEventsPaginatedQuery({
      variables: {
        filter: { event_account_id: itemLocation?.event_account?.id || '' },
        skip: 0,
        take: 1000,
      },
      skip: !itemLocation?.event_account?.id,
      errorPolicy: 'all',
      fetchPolicy: 'no-cache',
    });

  // Assets query is now handled within AssetsTable component

  const [updateItemLocation, { loading: updating }] =
    useUpdateItemLocationMutation({
      onCompleted: (d) => {
        setItemLocation(d.updateItemLocation);
      },
    });

  const [deleteItemLocation, { loading: deleting }] =
    useDeleteItemLocationMutation({
      onCompleted: () => {
        history.push(Routes.itemLocationSearch());
      },
      onError: (e) => {
        if (e.message === 'ITEM_LOCATION_USED') {
          LayoutStoreInstance.setError(i18n.t('ItemLocation:deleteErrorUsed'));
        } else {
          LayoutStoreInstance.setError(i18n.t('ItemLocation:deleteError'));
        }
      },
    });
  const [
    linkItemLocationToDeliveryAddress,
    { loading: linkingDeliveryAddress },
  ] = useLinkItemLocationToDeliveryAddressMutation();
  const [
    unlinkItemLocationFromDeliveryAddress,
    { loading: unlinkingDeliveryAddress },
  ] = useUnlinkItemLocationFromDeliveryAddressMutation();

  async function save() {
    if (itemLocation != null) {
      await updateItemLocation({
        variables: {
          id: itemLocation.id,
          name: itemLocation.name,
          type: itemLocationType,
          disabled: itemLocation.disabled_at != null,
        },
      });
    }
  }

  async function saveEventChange() {
    if (
      itemLocation != null &&
      selectedEvent !== null &&
      selectedEvent.length > 0
    ) {
      await updateItemLocation({
        variables: {
          id: itemLocation.id,
          name: itemLocation.name,
          event_id: selectedEvent,
        },
      });
    }
  }

  async function linkDeliveryAddress(nextDeliveryAddressId: string) {
    if (!itemLocation?.id || !nextDeliveryAddressId) {
      return;
    }
    if (nextDeliveryAddressId === linkedDeliveryAddressId) {
      return;
    }

    const previousDeliveryAddressId = linkedDeliveryAddressId || '';
    setSelectedDeliveryAddressId(nextDeliveryAddressId);
    try {
      const result = await linkItemLocationToDeliveryAddress({
        variables: {
          delivery_address_id: nextDeliveryAddressId,
          item_location_id: itemLocation.id,
        },
      });
      if (result.errors?.length) {
        throw new Error(result.errors[0]?.message || 'Failed to link address');
      }

      setLinkedDeliveryAddressId(nextDeliveryAddressId);
      const selectedAddress =
        deliveryAddressesResponse?.getDeliveryAddressesForEvent?.find(
          (address) => address.id === nextDeliveryAddressId,
        );
      setItemLocation((current) =>
        current
          ? {
              ...current,
              delivery_address: {
                id: nextDeliveryAddressId,
                name: selectedAddress?.name || '',
              },
            }
          : current,
      );
    } catch (error) {
      setSelectedDeliveryAddressId(previousDeliveryAddressId);
      LayoutStoreInstance.setError(
        error instanceof Error ? error.message : 'Failed to link address',
      );
    }
  }

  async function unlinkDeliveryAddress() {
    if (!itemLocation?.id || !linkedDeliveryAddressId) {
      return;
    }

    try {
      const result = await unlinkItemLocationFromDeliveryAddress({
        variables: {
          delivery_address_id: linkedDeliveryAddressId,
          item_location_id: itemLocation.id,
        },
      });
      if (result.errors?.length) {
        throw new Error(
          result.errors[0]?.message || 'Failed to unlink delivery address',
        );
      }

      setLinkedDeliveryAddressId(null);
      setSelectedDeliveryAddressId('');
      setItemLocation((current) =>
        current ? { ...current, delivery_address: null } : current,
      );
    } catch (error) {
      LayoutStoreInstance.setError(
        error instanceof Error ? error.message : 'Failed to unlink address',
      );
    }
  }

  if (loading || itemLocation == null) {
    return <ClassicLoadingSpinner />;
  }

  const itemLocationTypes = itemLocationTypesResponse?.getItemLocationTypes;
  const deliveryAddresses =
    deliveryAddressesResponse?.getDeliveryAddressesForEvent || [];
  const isDeliveryAddressActionLoading =
    linkingDeliveryAddress || unlinkingDeliveryAddress;

  const sortedEvents =
    eventsData?.eventsPaginated?.events
      ?.sort((a, b) => {
        if (!a || !a.name || !b || !b.name) {
          return 0;
        }
        return a?.name.localeCompare(b?.name);
      })
      ?.map(({ id, name }) => ({
        value: id,
        label: `${name} (${id})`,
      })) || [];

  return (
    <div>
      <MainHeader title={i18n.t('ItemLocation:itemLocation')} />
      <ContentSection title={i18n.t('ItemLocation:generalInfo')}>
        <div className="d-flex justify-content-end mb-2">
          <Button
            color="danger"
            disabled={deleting || readOnly}
            onClick={() => {
              setShowRemoveModal(true);
            }}
          >
            <FaTrash /> {i18n.t('Common:delete')}
          </Button>
        </div>

        <Modal
          isOpen={showRemoveModal}
          toggle={() => {
            setShowRemoveModal(false);
          }}
        >
          <ModalHeader
            toggle={() => {
              setShowRemoveModal(false);
            }}
          >
            {i18n.t('ItemLocation:modalTitle')}
          </ModalHeader>
          <ModalBody>
            {i18n.t('ItemLocation:modalBody')}: <p></p>
            {i18n.t('ItemLocation:itemLocation')}: <p>{itemLocation.name}</p>
            {i18n.t('Common:id')}: <p>{itemLocation.id}</p>
            {i18n.t('ItemLocation:eventAccountLink')}{' '}
            <p>{itemLocation?.event_account?.name || '-'}</p>
          </ModalBody>
          <ModalFooter>
            <Button
              color="danger"
              onClick={() => {
                deleteItemLocation({ variables: { id: itemLocation.id } });

                setShowRemoveModal(false);
              }}
            >
              {i18n.t('Common:yes')}
            </Button>{' '}
            <Button
              color="secondary"
              onClick={() => {
                setShowRemoveModal(false);
              }}
            >
              {i18n.t('Common:no')}
            </Button>
          </ModalFooter>
        </Modal>

        <div className="input-group mb-3">
          <span className="input-group-text" id="basic-addon1">
            {i18n.t('Common:name')}
          </span>
          <input
            className="form-control"
            disabled={readOnly}
            type="text"
            onChange={(e) =>
              setItemLocation({ ...itemLocation, name: e.target.value })
            }
            value={itemLocation.name}
          />
        </div>
        <div className="input-group mb-3">
          <span className="input-group-text" id="basic-addon1">
            {i18n.t('Common:type')}
          </span>
          <Input
            type="select"
            value={itemLocationType}
            onChange={(e) => {
              setItemLocationType(e.currentTarget.value);
            }}
            disabled={itemLocationTypesLoading || readOnly}
          >
            {itemLocationTypes?.map(({ id, type }) => {
              return <option key={id}>{type}</option>;
            })}
          </Input>
        </div>

        <div className="input-group mb-3">
          <span className="input-group-text">{i18n.t('Common:event')}</span>
          {!itemLocation.event && <Input type="text" disabled={true} />}
          {!!itemLocation.event && (
            <Link
              className="form-control"
              to={'/event/' + itemLocation.event.id}
            >
              {itemLocation.event?.name || itemLocation.event.id || ''}
            </Link>
          )}

          {editEvent && (
            <>
              <Select
                className="form-control"
                disabled={loadingEvents || readOnly}
                value={selectedEvent}
                onChange={(value) => {
                  setSelectedEvent(value);
                }}
                data={sortedEvents}
                searchable
                clearable
              />

              <Button
                color="primary"
                onClick={() => {
                  setEditEvent(false);
                  saveEventChange();
                }}
              >
                <FaSave />
                {' ' + i18n.t('Common:save')}
              </Button>
              <Button
                onClick={() => {
                  setEditEvent(false);
                }}
              >
                <FaLock />
                {' ' + i18n.t('Common:cancel')}
              </Button>
            </>
          )}

          {!editEvent && (
            <Button
              disabled={loadingEvents || updating}
              onClick={() => {
                setEditEvent(true);
              }}
            >
              <FaLock />
              {' ' + i18n.t('Common:edit')}
            </Button>
          )}
        </div>

        {itemLocation.event_account && (
          <div className="input-group mb-3">
            <span className="input-group-text">
              {i18n.t('ItemLocation:eventAccount')}
            </span>
            <Link
              className="form-control"
              to={'/event-account/' + itemLocation.event_account.id}
            >
              {itemLocation.event_account.name || ''}
            </Link>
          </div>
        )}

        <div className="input-group mb-3">
          <span className="input-group-text">
            {i18n.t('Order:deliveryAddress')}
          </span>
          <Input
            type="select"
            value={selectedDeliveryAddressId}
            onChange={(e) => {
              const nextDeliveryAddressId = e.currentTarget.value;
              if (!nextDeliveryAddressId) {
                return;
              }
              void linkDeliveryAddress(nextDeliveryAddressId);
            }}
            disabled={
              readOnly ||
              !itemLocation.event?.id ||
              deliveryAddressesLoading ||
              isDeliveryAddressActionLoading
            }
          >
            <option value="">-</option>
            {deliveryAddresses.map((address) => (
              <option key={address.id} value={address.id}>
                {address.name || address.id}
              </option>
            ))}
          </Input>
          <Button
            color="secondary"
            onClick={() => {
              void unlinkDeliveryAddress();
            }}
            disabled={
              readOnly ||
              !itemLocation.event?.id ||
              !linkedDeliveryAddressId ||
              isDeliveryAddressActionLoading
            }
          >
            {i18n.t('Event:unlink')}
          </Button>
        </div>

        <div className="form-check mb-3">
          <Input
            type="checkbox"
            className="form-check-input"
            id="itemLocationDisabled"
            checked={itemLocation.disabled_at != null}
            disabled={readOnly}
            onChange={(e) =>
              setItemLocation({
                ...itemLocation,
                disabled_at: e.currentTarget.checked
                  ? new Date().toISOString()
                  : null,
              })
            }
          />
          <label className="form-check-label" htmlFor="itemLocationDisabled">
            {i18n.t('ItemLocation:disabled')}
            {itemLocation.disabled_at != null &&
              ` (${new Date(itemLocation.disabled_at).toLocaleString()})`}
          </label>
        </div>

        <Button
          className="float-end mt-2"
          onClick={save}
          disabled={loading || updating || readOnly}
        >
          <FaSave className="me-1" />
          {loading || updating
            ? i18n.t('Common:saving')
            : i18n.t('Common:save')}
        </Button>
      </ContentSection>

      <ContentSection title={i18n.t('ItemLocation:stock')}>
        <ItemLocationStockView
          stock={stock}
          loadingStock={loadingStock}
          stockError={stockError}
          itemLocationName={itemLocation.name}
        />
      </ContentSection>
      <ContentSection title={i18n.t('ItemLocation:containerStationSection')}>
        <ItemLocationContainerStationView
          itemLocationId={locationId}
          itemLocationName={itemLocation.name}
        />
      </ContentSection>
      <ContentSection title={i18n.t('ItemLocation:assets')} collapsed={true}>
        <AssetsTable
          itemLocationId={locationId}
          itemLocationName={itemLocation.name}
        />
      </ContentSection>
      <ContentSection
        title={i18n.t('ItemLocation:orderFulfillments')}
        collapsed={true}
      >
        <OrderFulfillmentsTableWithFilter
          filter={{
            item_location_id: itemLocation.id,
            event_account_id: itemLocation.event_account?.id,
          }}
          authorizedSigners={[]}
          events={[]}
          itemLocations={[{ name: itemLocation.name, id: itemLocation.id }]}
          eventAccounts={[
            {
              name: itemLocation.event_account?.name || '',
              id: itemLocation.event_account?.id || '',
            },
          ]}
          headers={OfTableHeaderSort}
        />
      </ContentSection>
    </div>
  );
};

export default ItemLocationDetailView;
